import { ref, watch } from 'vue';
import { useStore } from 'vuex';

/**
 * Espelha `toggleSideBarAuto` / `restoreSidebarState` do layout legado
 * no estado local do shell V2 (`sidebarCollapsed` / `mobileOpen`).
 *
 * No legado, `is_show_sidebar = false` em desktop = menu recolhido.
 * No V2, isso corresponde a `sidebarCollapsed = true`.
 * Em mobile V2, o equivalente é fechar o drawer (`mobileOpen = false`).
 */
export function useDashboardV2SidebarAutoCollapse({
    sidebarCollapsed,
    mobileOpen,
    isMobile,
    flyoutGroupId = null,
}) {
    const store = useStore();
    const autoCollapsedByPage = ref(false);
    const sidebarPreviousCollapsed = ref(null);

    watch(
        () => ({
            show: store.state.is_show_sidebar,
            isUserChoice: store.state.sidebar_is_user_choice,
        }),
        (curr, prev) => {
            // Recolhimento automático (preview, ajuda, etc.)
            if (!curr.isUserChoice && !curr.show) {
                if (isMobile.value) {
                    mobileOpen.value = false;
                    return;
                }

                if (!autoCollapsedByPage.value) {
                    sidebarPreviousCollapsed.value = sidebarCollapsed.value;
                    autoCollapsedByPage.value = true;
                }

                sidebarCollapsed.value = true;
                if (flyoutGroupId) {
                    flyoutGroupId.value = null;
                }
                return;
            }

            // restoreSidebarState: escolha automática → restaura preferência anterior
            if (
                autoCollapsedByPage.value &&
                curr.isUserChoice &&
                prev &&
                !prev.isUserChoice
            ) {
                if (!isMobile.value && sidebarPreviousCollapsed.value !== null) {
                    sidebarCollapsed.value = sidebarPreviousCollapsed.value;
                }
                sidebarPreviousCollapsed.value = null;
                autoCollapsedByPage.value = false;
            }
        },
        { immediate: true }
    );

    /**
     * Se o utilizador interagir com o menu durante um recolhimento automático,
     * assume o controlo (o leave da página já não restaura o estado anterior).
     */
    function markSidebarAsUserChoice() {
        if (autoCollapsedByPage.value || !store.state.sidebar_is_user_choice) {
            autoCollapsedByPage.value = false;
            sidebarPreviousCollapsed.value = null;
            const show = isMobile.value ? !mobileOpen.value : !sidebarCollapsed.value;
            store.commit('toggleSideBar', show);
        }
    }

    return {
        autoCollapsedByPage,
        markSidebarAsUserChoice,
    };
}
